import {AppAction} from '../../../appActions';
import {AnalyticsPanelViewsActionsEnum} from './analyticsPanelViewsActionsTypes';
import {
  AnalyticsPanelViewsState,
  isAnalyticsViewSaved,
  isAnalyticsViewTemporary,
} from './analyticsPanelViewsState';

/*
 * Analytics views panel.
 */

export function analyticsPanelViewsReducer(
  state: AnalyticsPanelViewsState,
  action: AppAction,
): AnalyticsPanelViewsState {
  switch (action.type) {
    case AnalyticsPanelViewsActionsEnum.TOGGLE_VIEW_VISIBILITY: {
      const {savedViews, temporaryViews} = state;

      if (isAnalyticsViewSaved(action.payload)) {
        // Closed view was a saved view. Toggling off isDisplayed.
        const savedViewIndex = savedViews.findIndex((v) => v.id === action.payload);
        const newSavedViews = savedViews.map((item, index) => {
          if (index !== savedViewIndex) {
            // This isn't the item we care about - keep it as-is.
            return item;
          }

          // Otherwise, this is the one we want - return an updated value.
          return {
            ...item,
            isDisplayed: !item.isDisplayed,
          };
        });

        return {
          ...state,
          savedViews: newSavedViews,
        };
      }

      if (isAnalyticsViewTemporary(action.payload)) {
        // Closed view was a tmp view. Removing the view.
        const newTemporaryViews = temporaryViews.filter((tmp) => tmp.id !== action.payload);
        return {
          ...state,
          temporaryViews: newTemporaryViews,
        };
      }

      return {
        ...state,
      };
    }

    case AnalyticsPanelViewsActionsEnum.ADD_TEMPORARY_VIEW: {
      return {
        ...state,
        temporaryViews: action.payload,
        nbTemporaryViews: state.nbTemporaryViews + 1,
      };
    }

    case AnalyticsPanelViewsActionsEnum.REMOVE_TEMPORARY_VIEW: {
      const {temporaryViews} = state;
      return {
        ...state,
        temporaryViews: temporaryViews.filter((view) => view.id !== action.payload),
      };
    }

    case AnalyticsPanelViewsActionsEnum.FETCH_SAVED_VIEWS:
      return {
        ...state,
        isFetchingSavedViews: true,
      };

    case AnalyticsPanelViewsActionsEnum.FETCH_SAVED_VIEWS_COMPLETED:
      return {
        ...state,
        isFetchingSavedViews: false,
        savedViews: action.payload,
      };

    case AnalyticsPanelViewsActionsEnum.FETCH_SAVED_VIEWS_FAILED:
      return {
        ...state,
        isFetchingSavedViews: false,
        savedViews: [],
      };

    case AnalyticsPanelViewsActionsEnum.CREATE_SAVED_VIEW_FAILED: {
      return {
        ...state,
        error: action.payload,
      };
    }

    case AnalyticsPanelViewsActionsEnum.DELETE_SAVED_VIEW: {
      const {savedViews} = state;

      const newSavedViews = savedViews.filter((v) => v.id !== action.payload);

      return {
        ...state,
        savedViews: newSavedViews,
      };
    }

    case AnalyticsPanelViewsActionsEnum.UPDATE_SAVED_VIEW: {
      // Action used to update the view as an optimistic response. Content will get override when fetchViews is done.
      // @TODO handle same behaviour for other actions and handle error case (should reset the store to previous value).
      const {savedViews} = state;

      const savedViewIndex = savedViews.findIndex((v) => v.id === action.payload.id);
      const newSavedViews = savedViews.map((item, index) => {
        if (index !== savedViewIndex) {
          // This isn't the item we care about - keep it as-is.
          return item;
        }

        // Otherwise, this is the one we want - return an updated value.
        return {
          ...item,
          ...action.payload,
        };
      });

      return {
        ...state,
        savedViews: newSavedViews,
      };
    }

    case AnalyticsPanelViewsActionsEnum.CLEAN_ERROR: {
      return {
        ...state,
        error: undefined,
      };
    }

    default:
      return state;
  }
}
